import { contains, like } from '@infuse/api/common';
import { ChargingStation as ApiChargingStation, Evse as ApiEvse, Status } from '@infuse/api/cpms/operator';
import { searchInstallerChargingStations } from '@infuse/api/installer/charging-station';
import { Feature, useFeatureStore } from '@infuse/cpms/shared/features/store';
import { enumFromStringValue } from '@infuse/utils';
import { determineActionStatusAndFriendlyStatus, FriendlyStatus } from './actionStatus';
import { createSearchFilter, Options } from './filtering';
import {
  Availability,
  ChargingStation,
  ComponentStatus,
  ComponentSubStatus,
  Connector,
  ConnectorType,
  Current,
  Evse,
  EvseStatus,
  Protocol,
  SecurityProfile,
  StatusOverride
} from './model';

export const apiResultToDomain = (apiResult: ApiChargingStation): ChargingStation => {
  const [actionStatus, friendlyStatus] = determineActionStatusAndFriendlyStatus(apiResult);

  const status = useFeatureStore().hasFeature(Feature.ACTIVE_OFFLINE_DETECTION)
    ? apiResult.status
    : friendlyStatus === FriendlyStatus.OFFLINE
      ? Status.OFFLINE
      : apiResult.status;

  return {
    id: apiResult.id,
    accepted: apiResult.accepted,
    accessibility: apiResult.accessibility,
    reservable: apiResult.reservable,
    configured: apiResult.configured,
    blocked: apiResult.blocked,
    type: {
      model: apiResult.attributes.model,
      vendor: apiResult.attributes.vendorId
    },
    actionStatus: actionStatus,
    friendlyStatus: friendlyStatus,
    status: status,
    pool: {
      id: apiResult.locationId,
      name: apiResult.locationName
    },
    pools: apiResult.pools ?? [],
    firmwareVersion: apiResult.attributes.firmwareVersion,
    iccid: apiResult.attributes.iccid,
    imsi: apiResult.attributes.imsi,
    serialNumber: apiResult.attributes.chargingStationSerialNumber,
    evses: apiResult.evses
      ? apiResult.evses
          .map((evse) => apiEvseToDomainEvse(evse, apiResult.evses))
          .sort((e1: Evse, e2: Evse) => parseInt(e1.evseId) - parseInt(e2.evseId))
      : [],
    connectors: apiResult.evses
      ? apiResult.evses.map(apiEvseToDomainConnector).sort((a, b) => parseInt(a.evseId) - parseInt(b.evseId))
      : [],
    firstSeen: apiResult.firstSeen,
    lastSeen: apiResult.lastSeen,
    customIdentifier: apiResult.customIdentifier,
    protocol: apiResult.protocol
      ? enumFromStringValue(Protocol, apiResult.protocol) || Protocol.UNKNOWN
      : Protocol.UNKNOWN,
    securitySettings: {
      ipAddress: apiResult.securitySettings.ipAddress,
      passwordChangedAt: apiResult.securitySettings.passwordChangedAt,
      passwordStatus: apiResult.securitySettings.passwordStatus,
      securedConnection: apiResult.securitySettings.securedConnection,
      securityProfile: apiResult.securitySettings.securityProfile || SecurityProfile.PROFILE_0,
      webSocketAuthModeOverride: apiResult.securitySettings.authorizationMode,
      preferredOcppVersion: apiResult.securitySettings.preferredProtocol
        ? enumFromStringValue(Protocol, apiResult.securitySettings.preferredProtocol)
        : undefined
    },
    isOcpp1x:
      apiResult.protocol === Protocol.OCPPJ15 ||
      apiResult.protocol === Protocol.OCPPJ16 ||
      apiResult.protocol === Protocol.UNKNOWN ||
      apiResult.protocol === undefined,
    aggregateStatus: apiResult.aggregateStatus,
    aggregateSubStatus: apiResult.aggregateSubStatus,
    errorCode: apiResult.errorCode,
    errorInfo: apiResult.errorInfo,
    vendorErrorCode: apiResult.vendorErrorCode,
    lastStatusUpdate: apiResult.lastStatusUpdate
  };
};

const apiEvseToDomainEvse = (evse: ApiEvse, evses: ApiEvse[]): Evse => {
  return {
    id: evse.id,
    evseId: evse.evseId,
    ocppEvseId: evse.ocppEvseId,
    capabilities: evse.capabilities || [],
    floorLevel: evse.floorLevel,
    physicalReference: evse.physicalReference,
    parkingRestrictions: evse.parkingRestrictions || [],
    evseStatus: EvseStatus[evse.evseStatus] || EvseStatus.ACTIVE,
    connectors:
      evses
        .filter((e) => e.evseId === evse.evseId)
        .map(apiEvseToDomainConnector)
        .sort((a, b) => parseInt(a.evseId) - parseInt(b.evseId)) || []
  };
};

const apiEvseToDomainConnector = (evse: ApiEvse): Connector => {
  return {
    id: evse.id,
    evseId: evse.evseId,
    status: evse.status ? ComponentStatus[evse.status as keyof typeof ComponentStatus] : ComponentStatus.UNKNOWN,
    subStatus: evse.subStatus ? ComponentSubStatus[evse.subStatus] : undefined,
    statusOverride: evse.statusOverride ? StatusOverride[evse.statusOverride] : undefined,
    availability: Availability[evse.availability] || Availability.INOPERATIVE,
    evseStatus: EvseStatus[evse.evseStatus] || EvseStatus.ACTIVE,
    connectorId: evse.connectors[0]?.connectorId,
    maxAmp: evse.connectors[0]?.maxAmp,
    phase: evse.connectors[0]?.phase,
    voltage: evse.connectors[0]?.voltage,
    maxElectricPower: evse.connectors[0]?.maxElectricPower,
    current: evse.connectors[0]?.current ? Current[evse.connectors[0].current] : Current.UNSPECIFIED,
    type: ConnectorType[evse.connectors[0]?.connectorType] || ConnectorType.UNSPECIFIED,
    physicalReference: evse.connectors[0]?.physicalReference || '',
    errorCode: evse.connectors[0]?.errorCode,
    errorInfo: evse.connectors[0]?.errorInfo,
    vendorErrorCode: evse.connectors[0]?.vendorErrorCode
  };
};

export async function enrichWithInstallerAliases(
  responseItems: ChargingStation[],
  options: Options
): Promise<ChargingStation[]> {
  if (responseItems.length === 0) {
    return responseItems;
  }

  const chargingStationIds = responseItems.map((cs) => cs.id);
  if (chargingStationIds.length === 0) {
    return responseItems;
  }

  let filter = contains('chargingStation.id', chargingStationIds);
  if (options.query) {
    const searchFilter = createSearchFilter(options.query, (part) =>
      like('lower(chargingStation.id)', part).or(like('lower(alias)', part))
    );
    if (searchFilter) {
      filter = filter.and(searchFilter);
    }
  }

  const installerChargingStations = await searchInstallerChargingStations({
    size: chargingStationIds.length,
    filter
  });

  const installerContent = installerChargingStations.content ?? [];
  return responseItems.flatMap((cs) => {
    const installerCs = installerContent.find((i) => i.id === cs.id);
    return installerCs ? [{ ...cs, customIdentifier: installerCs.alias }] : [];
  });
}
